import {toast} from 'react-toastify';

/**
 * Tells a long-lived tab that the dashboard has been redeployed.
 *
 * The dashboard is a single-page app: an admin who signed in this morning is
 * still running the bundle they downloaded then, however many deploys have
 * landed since. Two things go wrong because of that — they keep using old code
 * against a moved API, and because every route is lazy-loaded, opening a route
 * whose chunk the new container no longer has throws a chunk-load error and
 * leaves a blank screen.
 *
 * Detection needs no backend support: CRA writes `build/asset-manifest.json`
 * with the hashed bundle names, and the express server already serves it. If
 * the `main.js` in the deployed manifest is not the one this tab loaded, a new
 * build is live.
 *
 * The prompt is deliberately not an automatic reload — an admin may be halfway
 * through a form, and losing that to a background deploy is worse than running
 * yesterday's bundle for another minute. The one case that does reload itself
 * is a missing chunk, where the screen is already broken.
 */

const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 5 * 60 * 1000;
const UPDATE_TOAST_ID = 'app-update-available';
const RELOAD_STAMP = 'app-chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60 * 1000;

const MAIN_BUNDLE = /\/static\/js\/main\.[^/]+\.js$/;

const toPath = src => {
  try {
    return new URL(src, window.location.origin).pathname;
  } catch (e) {
    return null;
  }
};

/**
 * The main bundle this tab is running, read off its own script tag — the only
 * source that can't disagree with what is actually executing.
 */
export const loadedMainBundle = (doc = document) => {
  const src = [...doc.querySelectorAll('script[src]')]
    .map(script => script.getAttribute('src'))
    .find(value => MAIN_BUNDLE.test(value || ''));
  return src ? toPath(src) : null;
};

/** The main bundle the server is serving right now. */
export const fetchDeployedMainBundle = async () => {
  // `no-store` plus the timestamp: the manifest is served with an ETag, and a
  // 304 from an intermediate cache would make every deploy invisible.
  const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!response.ok) return null;
  const manifest = await response.json();
  const file = manifest?.files?.['main.js'];
  return file ? toPath(file) : null;
};

// Both sides must be known before this means anything: a failed fetch or a
// manifest-less build is "no information", never "there is a new version".
export const isNewDeploy = (loaded, deployed) =>
  !!loaded && !!deployed && loaded !== deployed;

const announceUpdate = () =>
  toast.info(
    <span>
      A new version of the dashboard is available.{' '}
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#8d6cfa',
          fontWeight: 600,
          cursor: 'pointer',
        }}>
        Refresh
      </button>
    </span>,
    {
      toastId: UPDATE_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    },
  );

/**
 * Polls for a new build and prompts once. Returns a function that stops it.
 *
 * The check also runs whenever the tab is brought back to the front, which is
 * when an admin is most likely to be on a stale build — a tab left open over
 * lunch, or overnight.
 */
export const watchForNewVersion = ({
  intervalMs = POLL_MS,
  enabled = process.env.NODE_ENV === 'production',
  announce = announceUpdate,
} = {}) => {
  const loaded = enabled ? loadedMainBundle() : null;
  // Nothing to compare against (dev server, or a build without the manifest):
  // stay quiet rather than prompt on a guess.
  if (!loaded) return () => {};

  let announced = false;

  const check = async () => {
    if (announced || document.visibilityState === 'hidden') return;
    const deployed = await fetchDeployedMainBundle().catch(() => null);
    if (isNewDeploy(loaded, deployed)) {
      announced = true;
      announce();
    }
  };

  const timer = setInterval(check, intervalMs);
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

const CHUNK_ERROR =
  /loading chunk \S+ failed|loading css chunk|chunkloaderror|failed to fetch dynamically imported module/i;

export const isChunkLoadError = error =>
  CHUNK_ERROR.test(error?.message || String(error || ''));

/**
 * Reloads the tab once when a lazy route's chunk is gone.
 *
 * After a deploy the new container has differently-hashed chunks, so the names
 * this tab remembers 404. One reload picks up the new index.html and the chunk
 * names that go with it. The timestamp stops a reload loop if the chunk is
 * missing for some other reason — at most one reload a minute, then the error
 * surfaces normally.
 */
export const reloadOnMissingChunk = ({
  storage = window.sessionStorage,
  reload = () => window.location.reload(),
} = {}) => {
  const handle = event => {
    if (!isChunkLoadError(event?.error || event?.reason || event)) return;
    let last = 0;
    try {
      last = Number(storage.getItem(RELOAD_STAMP)) || 0;
      if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
      storage.setItem(RELOAD_STAMP, String(Date.now()));
    } catch (e) {
      // Private mode / blocked storage: reloading once is still better than a
      // blank screen, so carry on without the loop guard.
    }
    reload();
  };

  window.addEventListener('error', handle);
  window.addEventListener('unhandledrejection', handle);

  return () => {
    window.removeEventListener('error', handle);
    window.removeEventListener('unhandledrejection', handle);
  };
};
